import React, { useCallback, ReactElement } from 'react';
import Table from '../../../../components/table/table.component';
import TableHead from '../../../../components/table-head/table-head.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import CheckAccess from '../../../../components/check-access/check-access.component';
import ChipLink from '../../../../components/chip-link/chip-link.component';
import Tooltip from '../../../../components/tooltip/tooltip.component';
import TooltipDialog from '../../../../components/tooltip-dialog/tooltip-dialog.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import PaddedFlexContainer from '../../../../components/padded-flex-container/padded-flex-container.component';
import ScrollableContainer from '../../../../components/scrollable-container/scrollable-container.component';
import TextItalic from '../../../../components/text-italic/text-italic.component';
import ActionButton from '../../../../components/action-button/action-button.component';
import FilteredText from '../../../../components/filtered-text/filtered-text.component';
import FileLink from '../../../../components/file-link/file-link.component';
import RichTextBlock from '../../../../components/rich-text-block/rich-text-block.component';
import TableRowNoContent from '../../../../components/table-row-no-content/table-row-no-content.component';
import EditIcon from '../../../../icons/edit-icon';
import DeleteItemIcon from '../../../../icons/delete-item-icon';
import CommentService from '../../../../services/comment-service';
import FormatService from '../../../../services/format-service';
import { ACCOUNT_USER_TYPE_LIST_ADMIN } from '../../../../constants/entity/account-user-type-lists';
import { CONTACT_TYPE_COMPANY, CONTACT_TYPE_PERSON } from '../../../../constants/entity/contact-types';
import { CONTACT_VIEW } from '../../../../constants/entity/permissions';
import { DATA_ATTACHMENT_ID, DATA_IS_ANALYZABLE } from '../../../../constants/system/data-attributes';
import { MIME_TYPE_APPLICATION_PDF } from '../../../../constants/system/mime-types';
import COMMENT_TYPE_SYSTEM from '../../../../constants/entity/comment-types';
import PersonDefaultImage from '../../../../static/images/person-default-image.png';
import CompanyDefaultImage from '../../../../static/images/company-default-image.png';
import AssetDefaultImage from '../../../../static/images/asset-default-image.png';
import NoImage from '../../../../static/images/no-image.png';

const CommentTable: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COMMENT_ENTITY_TYPE_ACTIVITY: string = 'activity';
  const COMMENT_ENTITY_TYPE_CONTACT: string = 'contact';

  const {
    withNoHeader,
    withAttachmentActionMenu,
    commentList,
    contentHeight,
    currentUser,
    queryCommentId,
    commentActiveId,
    commentEditId,
    labelIconMap,
    editPermissions,
    deletePermissions,
    mentionPermission,
    hashtagPermission,
    attachmentActionMenuPermissions,
    attachmentActionMenuItems,
    onSwitchCommentEditMode,
    onDeleteCommentConfirm,
    onDeleteCommentCancel,
    onCommentActiveIdChange,
    onCommentEntityLinkClick,
    onCommentAttachmentFileViewClick,
    onCommentAttachmentFileOpenActionMenu,
    onCheckIsCommentAttachmentFileMenuItemVisible,
  } = props;

  const isCurrentUserAdmin: boolean = !!currentUser && !!currentUser.accountUserType
    && ACCOUNT_USER_TYPE_LIST_ADMIN.includes(currentUser.accountUserType);
  const isEmpty: boolean = !commentList || !commentList.length;

  const commentEntityLinkClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, link: string,
  ): void => {
    if (onCommentEntityLinkClick) {
      onCommentEntityLinkClick(link);
    }
  }, []);

  const switchCommentEditMode: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, commentItem: Comment,
  ): void => {
    if (onSwitchCommentEditMode) {
      onSwitchCommentEditMode(event, commentItem);
    }
  }, [commentEditId]);

  const getCommentEntityDefaultImage: Function = useCallback((commentEntity: CommentEntity): string => {
    if (commentEntity.type === COMMENT_ENTITY_TYPE_ACTIVITY) {
      return AssetDefaultImage;
    }

    if (commentEntity.type === COMMENT_ENTITY_TYPE_CONTACT && commentEntity.subType) {
      if (commentEntity.subType === CONTACT_TYPE_COMPANY) {
        return CompanyDefaultImage;
      }

      if (commentEntity.subType === CONTACT_TYPE_PERSON) {
        return PersonDefaultImage;
      }
    }

    return NoImage;
  }, []);

  const checkIsFileAnalyzable: Function = useCallback((file?: FileItem): boolean => {
    return !!file && !!file.mimeType && file.mimeType === MIME_TYPE_APPLICATION_PDF;
  }, []);

  return (
    <ScrollableContainer
      id={CommentService.ID_COMMENT_CONTAINER}
      isEmpty={isEmpty}
      maxHeight={contentHeight || undefined}
    >
      <Table withStickyHeader={!withNoHeader}>
        {
          !withNoHeader && (
            <TableHead isSubHeader>
              <TableRow>
                <TableCell>
                  Comments
                </TableCell>
              </TableRow>
            </TableHead>
          )
        }
        <TableBody isPlain withZebraRows>
          {
            commentList && !!commentList.length ? commentList.map((commentItem: Comment): ReactElement => (
              <TableRow
                key={commentItem.id}
                id={`${CommentService.ID_PREFIX_COMMENT_TABLE_ROW}-${commentItem.id}`}
                className={CommentService.CLASS_NAME_COMMENT_TABLE_ROW}
              >
                <TableCell
                  isActive={commentItem.id === queryCommentId || commentItem.id === commentEditId}
                  isDark={commentItem.type === COMMENT_TYPE_SYSTEM}
                >
                  {
                    commentItem.type !== COMMENT_TYPE_SYSTEM && commentItem.user && currentUser
                    && (commentItem.user.id === currentUser.id || isCurrentUserAdmin) && (
                      <PaddedFlexContainer isFloatRight>
                        <CheckAccess permissions={editPermissions}>
                          <Tooltip title={commentEditId && commentEditId === commentItem.id ? 'View' : 'Edit'}>
                            <ActionButton
                              withNoHighlight
                              withSmallPadding
                              id={`${CommentService.ID_PREFIX_COMMENT_EDIT_BUTTON}-${commentItem.id}`}
                              isActive={commentEditId && commentEditId === commentItem.id}
                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                switchCommentEditMode(event, commentItem);
                              }}
                            >
                              <EditIcon />
                            </ActionButton>
                          </Tooltip>
                        </CheckAccess>
                        <CheckAccess permissions={deletePermissions}>
                          <Tooltip
                            withArrow
                            withLeftEndPlacement
                            isOpen={!!commentActiveId && commentActiveId === commentItem.id}
                            component={(
                              <TooltipDialog
                                title="Are you sure you want to delete this comment?"
                                onConfirm={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                  onDeleteCommentConfirm(event, commentItem.id);
                                }}
                                onCancel={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                  onDeleteCommentCancel(event, commentItem.id);
                                }}
                              />
                            )}
                          >
                            <ActionButton
                              withNoHighlight
                              withSmallPadding
                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                onCommentActiveIdChange(event, commentItem.id);
                              }}
                            >
                              <DeleteItemIcon />
                            </ActionButton>
                          </Tooltip>
                        </CheckAccess>
                      </PaddedFlexContainer>
                    )
                  }
                  {
                    !!commentItem.label && !!labelIconMap && !!labelIconMap[commentItem.label.name] && (
                      <PaddedFlexContainer isDefault isFloatLeft paddingTop={2} paddingRight={10} paddingBottom={5}>
                        <TextBlock>
                          <Tooltip title={commentItem.label.title}>
                            {labelIconMap[commentItem.label.name]}
                          </Tooltip>
                        </TextBlock>
                      </PaddedFlexContainer>
                    )
                  }
                  <RichTextBlock>
                    <FilteredText
                      withMentions
                      withHashtags
                      text={commentItem.text}
                      mentionPermission={mentionPermission}
                      hashtagPermission={hashtagPermission}
                    />
                  </RichTextBlock>
                  {
                    commentItem.attachments && !!commentItem.attachments.length
                    && commentItem.attachments.map((attachmentItem: Attachment): ReactElement => (
                      <PaddedFlexContainer key={attachmentItem.id}>
                        <FileLink
                          withActionMenu={withAttachmentActionMenu}
                          fileLink={attachmentItem.file}
                          actionProps={{
                            [DATA_ATTACHMENT_ID]: attachmentItem.id,
                            [DATA_IS_ANALYZABLE]: checkIsFileAnalyzable(attachmentItem.file)
                              ? FormatService.BOOLEAN_VALUE_TRUE : null,
                          }}
                          actionMenuItems={attachmentActionMenuItems}
                          actionMenuPermissions={attachmentActionMenuPermissions}
                          entityTypeProp={DATA_IS_ANALYZABLE}
                          onMouseDown={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            if (onCommentAttachmentFileViewClick) {
                              onCommentAttachmentFileViewClick(event, attachmentItem.file);
                            }
                          }}
                          onOpenActionMenu={onCommentAttachmentFileOpenActionMenu}
                          onCheckIsMenuItemVisible={onCheckIsCommentAttachmentFileMenuItemVisible}
                        />
                      </PaddedFlexContainer>
                    ))
                  }
                  <FlexContainer isEnd>
                    <TextItalic>
                      {commentItem.type !== COMMENT_TYPE_SYSTEM && commentItem.user
                        ? `By ${commentItem.user.fullName} ${commentItem.date}` : commentItem.date}
                    </TextItalic>
                  </FlexContainer>
                  <div>
                    {
                      commentItem.linkedEntities && !!commentItem.linkedEntities.length
                      && commentItem.linkedEntities.map((commentEntityItem: CommentEntity): ReactElement => {
                        const defaultImage: string = getCommentEntityDefaultImage(commentEntityItem);

                        return (
                          <ChipLink
                            key={commentEntityItem.id}
                            label={commentEntityItem.name}
                            imageLink={commentEntityItem.photoThumbnail || commentEntityItem.photo || defaultImage}
                            permission={CONTACT_VIEW}
                            onClick={commentEntityItem.link ? (event: React.MouseEvent<Element, MouseEvent>): void => {
                              commentEntityLinkClick(event, commentEntityItem.link);
                            } : undefined}
                          />
                        );
                      })
                    }
                  </div>
                </TableCell>
              </TableRow>
            )) : (
              <TableRowNoContent>
                <TableCell>
                  No comments yet
                </TableCell>
              </TableRowNoContent>
            )
          }
        </TableBody>
      </Table>
    </ScrollableContainer>
  );
});

export default CommentTable;
